import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { Link } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroller';
import ReactLoading from 'react-loading';

import DropdownFilter from 'ui_system/dropdowns/dropdown_filter/DropdownFilter';
import { Theme } from 'ui_system/theme';

import {
  fetchSlots,
  fetchMoreSlots,
  updateSlotFilter,
} from 'data_system/resource_portal/actions/slotsActions';

import { getUser, getBrand } from 'data_system/token_auth/selectors';
import { getResourceTimezone } from 'data_system/resource_portal/selectors';

import { t } from 'services/translation';
import Notice from '../../components/Notice';
import SlotRow, { LoadingRow } from './components/SlotRow';
import { List, Empty } from './styles';

const Container = styled.div``;
const DropdownFilterContainer = styled.div`
  display: flex;
  align-items: center;
  height: 65px;
  padding-left: 20px;
  border-bottom: 1px solid ${Theme.colors.lightLine};
`;

const loader = (
  <ReactLoading
    key={0}
    type="bubbles"
    delay={0}
    height={60}
    width={60}
    color={Theme.colors.lightText}
    css={css`
      margin: 0 auto;
    `}
  />
);

const SlotList = () => {
  const resource = useSelector(getUser);
  const i18n =
    resource && resource.data && resource.data.attributes
      ? resource.data.attributes.i18n
      : '';

  const dispatch = useDispatch();
  const { name } = useSelector(getBrand);
  const resourceTimezone = useSelector(getResourceTimezone);
  const { slotNotice, slotNoticeType } = useSelector((state) => state.slots);

  const {
    slots,
    slotsFetching,
    slotFilter,
    page,
    totalPages,
    moreSlotsFetching,
  } = useSelector((state) => state.slots);

  useEffect(() => {
    document.title = `${name} - ${
      slotFilter.charAt(0).toUpperCase() + slotFilter.substring(1)
    } Classes`;

    if (slots && slots.length === 0) dispatch(fetchSlots());
  }, [slotFilter]);

  const renderList = () => {
    if (slotsFetching) {
      return [...Array(10).keys()].map((key) => <LoadingRow key={key} />);
    }

    if (slots && slots.length === 0) {
      return <Empty>{t(`YOU_HAVE_NO_${slotFilter}_EXPERIENCES`)}</Empty>;
    }

    const slotsList = slots.map((slot) => {
      const { id } = slot;
      return (
        <Link
          key={`${id}`}
          to={{
            pathname: `/scheduled/${id}`,
            state: { navigatedFromList: true },
          }}
        >
          <SlotRow {...slot.attributes} resourceTimezone={resourceTimezone} />
        </Link>
      );
    });

    return (
      <List>
        <InfiniteScroll
          pageStart={1}
          loadMore={() => dispatch(fetchMoreSlots())}
          hasMore={
            slotsList.length >= 20 && page <= totalPages && !moreSlotsFetching
          }
        >
          {slotsList}
          {moreSlotsFetching ? loader : null}
        </InfiniteScroll>
      </List>
    );
  };

  return (
    <Container>
      <DropdownFilterContainer>
        <DropdownFilter
          filter={slotFilter}
          selectFilter={(filter) => dispatch(updateSlotFilter(filter))}
          i18n={i18n}
          dropdownOptions={[
            { key: 'upcoming', label: t('UPCOMING') },
            { key: 'past', label: t('PAST') },
          ]}
        />
      </DropdownFilterContainer>
      <Notice type={slotNoticeType} text={slotNotice} show={!!slotNotice} />
      {renderList()}
    </Container>
  );
};

export default SlotList;
